import { useNavigate } from "react-router-dom";
import { Home, Ticket, CalendarDays, CalendarClock, Users, BarChart3, Settings as SettingsIcon, Plus, LogOut, PhoneOff, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { GlobalSearch } from "@/components/GlobalSearch";
import { useAuth } from "@/contexts/AuthContext";

const NAV = [
  { key: "home", label: "Home", icon: Home },
  { key: "tours", label: "Tours", icon: Ticket },
  { key: "calendar", label: "Calendar", icon: CalendarDays },
  { key: "future", label: "Future Requests", icon: CalendarClock },
  { key: "guests", label: "Guests", icon: Users },
  { key: "insights", label: "Insights", icon: BarChart3 },
  { key: "callouts", label: "Time & Attendance", icon: PhoneOff },
  { key: "settings", label: "Settings", icon: SettingsIcon },
  { key: "admin", label: "Admin", icon: ShieldCheck, adminOnly: true },
];

// Desktop-only left sidebar (lg+): a persistent, always-open version of the right-side
// hamburger drawer — global search, Add tour, all sections, and the account footer.
// On phones/tablets the bottom pill nav + hamburger drawer are used instead.
export const SideNav = ({ tab, onNavigate, homeBadge = 0, pendingCount = 0 }) => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const addTour = () => navigate("/guest/new");

  return (
    <aside
      className="fixed left-0 top-0 z-40 hidden h-screen w-64 flex-col border-r border-zinc-800/80 bg-zinc-950 lg:flex"
      data-testid="side-nav"
    >
      <div className="border-b border-zinc-800/80 p-4">
        <button
          onClick={() => onNavigate("home")}
          className="mb-3 flex items-center gap-2.5"
          data-testid="side-nav-brand"
        >
          <img src="/icon-512.png" alt="Stage Door" draggable={false} className="h-9 w-9 select-none rounded-xl ring-1 ring-white/10" />
          <div className="min-w-0 text-left">
            <p className="truncate font-display text-sm font-700 text-zinc-100">Stage Door</p>
            <p className="text-[11px] uppercase tracking-wider text-[#E0575B]">VIP All-In-One</p>
          </div>
        </button>
        <GlobalSearch variant="full" />
        <Button onClick={addTour} className="mt-3 w-full bg-[#9B2226] text-white hover:bg-[#7f1c1f]" data-testid="side-nav-add-tour">
          <Plus className="mr-1.5 h-4 w-4" /> Add tour
        </Button>
      </div>

      <nav className="flex-1 overflow-y-auto p-3">
        {NAV.filter((item) => !item.adminOnly || user?.role === "admin").map((item) => {
          const Icon = item.icon;
          const active = tab === item.key;
          const showAdminBadge = item.key === "admin" && pendingCount > 0;
          const showHomeBadge = item.key === "home" && homeBadge > 0;
          return (
            <button
              key={item.key}
              onClick={() => onNavigate(item.key)}
              data-testid={`side-nav-${item.key}`}
              className={`relative mb-1 flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left text-sm font-500 transition-all duration-200 ${active ? "bg-[#9B2226]/20 text-[#E0575B]" : "text-zinc-300 hover:translate-x-0.5 hover:bg-zinc-800/70 hover:text-zinc-100"}`}
            >
              {active && <span className="absolute left-0 top-1/2 h-6 w-1 -translate-y-1/2 rounded-r-full bg-[#E0575B]" />}
              <Icon className={`h-5 w-5 ${active ? "text-[#E0575B]" : "text-zinc-400"}`} />
              <span className="flex-1">{item.label}</span>
              {showAdminBadge && (
                <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-amber-500 px-1.5 text-[11px] font-700 text-zinc-950" data-testid="side-nav-admin-badge">
                  {pendingCount}
                </span>
              )}
              {showHomeBadge && (
                <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-[#9B2226] px-1.5 text-[11px] font-700 text-white" data-testid="side-nav-home-badge">
                  {homeBadge}
                </span>
              )}
            </button>
          );
        })}
      </nav>

      <div className="border-t border-zinc-800/80 p-4">
        <div className="mb-3 flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-full bg-[#9B2226]/20 text-sm font-700 text-[#E0575B]">
            {(user?.name || user?.email || "?").trim().charAt(0).toUpperCase()}
          </div>
          <div className="min-w-0">
            <p className="truncate text-sm font-500 text-zinc-100" data-testid="side-nav-user-name">{user?.name || "Signed in"}</p>
            {user?.email && <p className="truncate text-xs text-zinc-500">{user.email}</p>}
          </div>
        </div>
        <Button onClick={() => logout()} variant="outline" className="w-full border-zinc-700 bg-transparent text-zinc-300 hover:bg-zinc-800 hover:text-white" data-testid="side-nav-signout">
          <LogOut className="mr-1.5 h-4 w-4" /> Sign out
        </Button>
      </div>
    </aside>
  );
};
